import React from 'react'
import { useWatch } from 'react-hook-form'
import {
  ControlledTextInput,
  ControlledDatePicker,
  ControlledFunctionalTag,
  ControlledSelect,
} from 'transfix-form/components'
import ControlledTimeInput from 'transfix-form/components/ControlledTimeInput'
import { Button } from 'transfix-ui/components/Button'
import { Card } from 'transfix-ui/components/Card'
import { TAG_SIZE } from 'transfix-ui/components/Tag'
import { TIME_INPUT_FORMAT } from 'transfix-ui/components/TimeInput'
import { Box } from 'transfix-ui/core'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { ITrailerStatesEnum, IShipmentStates } from 'types/graphqlTypes'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import {
  trailerStatusOptions,
  trailerStatusOptionsAssignedInProgress,
  trailerStatusOptionsAssignedDelivered,
  trailerStatusOptionsInactive,
  trailerStatusOptionsUnassigned,
  trailerStatusOptionsEmptyAtDelivery,
  trailerStatusOptionsAssignedToFacility,
  statusTagMap,
  ITrailerStatusTag,
} from '../../equipment-listing/schema/TrailerSchema'
import { useEditAssignmentForm } from '../hooks/useEditAssignmentForm'
import { IAssignmentSchema, TFacilityAutocompleteInput } from '../schema/AssignmentSchema'
import { ControlledAutocompleteFacility } from './ControlledAutocompleteFacility'
import { ControlledAutocompleteLoad } from './ControlledAutocompleteLoad'

const useStyles = makeStyles(() => ({
  timeInput: {
    '& .text-input-container': {
      width: '100%',
    },
  },
  submitButton: {
    '& button': {
      width: '100%',
    },
  },
  statusTag: {
    '& .dpl-tag-dark': {
      backgroundColor: '#D3D9E0',
      borderColor: brandColors.coolGray3,
    },
    '& .dpl-tag-dark:hover': {
      borderColor: `${brandColors.coolGray3} !important`,
    },
    '& .dpl-functional-tag > .dpl-functional-tag-trigger.dpl-functional-tag-trigger-dark': {
      backgroundColor: brandColors.coolGray3,
      borderColor: brandColors.coolGray3,
    },
    '& .dpl-functional-tag > .dpl-functional-tag-trigger.dpl-functional-tag-trigger-dark:hover': {
      backgroundColor: brandColors.coolGray8,
      borderColor: brandColors.coolGray8,
    },
    '& .dpl-functional-tag > .dpl-functional-tag-trigger.dpl-functional-tag-trigger-dark:focus': {
      backgroundColor: brandColors.coolGray8,
      borderColor: brandColors.skyBlue2,
    },
  },
}))

export interface ITrailerAssignmentCardProps {
  /**
   * Active Facility object assigned to Trailer
   * @example { id: '123', text: 'Example Facility' }
   */
  activeFacility?: TFacilityAutocompleteInput
  /**
   * Empty Trailer Arrival Date
   * @example '2022-10-11T00:00:00.000'
   */
  emptyTrailerArrivalDate?: string
  /**
   * Empty Trailer Arrival Time
   * @example '12:00'
   */
  emptyTrailerArrivalTime?: string
  /**
   * Empty Trailer Ready Date
   * @example '2022-10-11T00:00:00.000'
   */
  emptyTrailerReadyDate?: string
  /**
   * Empty Trailer Ready Time
   * @example '12:00'
   */
  emptyTrailerReadyTime?: string
  /**
   * Trailer note
   * @example 'Exampe note'
   */
  note?: string
  /**
   * Status of shipment
   * @example 'delivered'
   */
  shipmentStatus?: string
  /**
   * Card Title
   * @example 'Assignment'
   */
  title?: string
  /**
   * Status of trailer
   * @example { value: ITrailerStatesEnum.inactive, label: 'Inactive', style: TAG_STYLE.dark }
   */
  trailerStatus?: ITrailerStatusTag
  /**
   * On submit handler
   * @example (formParams) => {}
   */
  motorCarrierId: string
  onSubmit: (formParams: IAssignmentSchema) => {}
}

const TrailerAssignmentCard = ({
  activeFacility,
  emptyTrailerArrivalDate,
  emptyTrailerArrivalTime,
  emptyTrailerReadyDate,
  emptyTrailerReadyTime,
  note,
  shipmentStatus,
  motorCarrierId,
  title = 'Assignment',
  trailerStatus = statusTagMap.inactive,
  onSubmit,
}: ITrailerAssignmentCardProps) => {
  const { control, errors, handleSubmit } = useEditAssignmentForm({
    defaultValues: {
      activeFacility,
      emptyTrailerArrivalDate: emptyTrailerArrivalDate || null,
      emptyTrailerArrivalTime,
      emptyTrailerReadyDate: emptyTrailerReadyDate || null,
      emptyTrailerReadyTime,
      notes: note,
      status: statusTagMap[trailerStatus.value],
    },
  })
  const selectedStatus = useWatch<ITrailerStatusTag>({
    control,
    name: 'status',
  })
  const selectedFacility = useWatch<TFacilityAutocompleteInput>({
    control,
    name: 'activeFacility',
  })

  const classes = useStyles()

  // TODO: submit assignment
  const submitHandler = (formParams: IAssignmentSchema) => {
    onSubmit(formParams)
  }

  const showActiveFacility =
    selectedStatus &&
    [ITrailerStatesEnum.assigned_to_facility, ITrailerStatesEnum.empty_at_delivery].includes(
      selectedStatus.value as ITrailerStatesEnum
    )
  const showEmptyTrailerArrival =
    selectedStatus && ITrailerStatesEnum.assigned_to_facility === selectedStatus.value
  const showEmptyTrailerReady =
    selectedStatus && ITrailerStatesEnum.empty_at_delivery === selectedStatus.value
  const showLoadNumber =
    selectedStatus &&
    ITrailerStatesEnum.assigned_to_shipment === selectedStatus.value &&
    trailerStatus.value !== ITrailerStatesEnum.assigned_to_shipment

  const getStatusOptions = () => {
    if (!trailerStatus) {
      return [...trailerStatusOptions]
    }

    if (
      trailerStatus.value === ITrailerStatesEnum.assigned_to_shipment &&
      shipmentStatus !== IShipmentStates.delivered
    ) {
      return [...trailerStatusOptionsAssignedInProgress]
    }

    switch (trailerStatus.value) {
      case ITrailerStatesEnum.unassigned:
        return [...trailerStatusOptionsUnassigned]
      case ITrailerStatesEnum.inactive:
        return [...trailerStatusOptionsInactive]
      case ITrailerStatesEnum.assigned_to_facility:
        return [...trailerStatusOptionsAssignedToFacility]
      case ITrailerStatesEnum.assigned_to_shipment:
        return [...trailerStatusOptionsAssignedDelivered]
      case ITrailerStatesEnum.empty_at_delivery:
        return [...trailerStatusOptionsEmptyAtDelivery]
      default:
        return [...trailerStatusOptions]
    }
  }

  return (
    <Card title={title}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <Box display='flex' flexDirection='column'>
          <Box marginBottom={2} className={classes.statusTag}>
            <ControlledFunctionalTag
              name='status'
              control={control}
              FunctionalTagProps={{
                options: getStatusOptions(),
                size: TAG_SIZE.small,
              }}
            />
          </Box>
          {showActiveFacility && (
            <div>
              <Box marginBottom={2}>
                <ControlledSelect
                  name='customer'
                  control={control}
                  defaultValue='Transfix'
                  SelectProps={{
                    label: 'Customer',
                    disabled: true,
                    options: [{ label: 'Transfix', value: 'Transfix' }],
                  }}
                />
              </Box>
              <Box marginBottom={2}>
                <ControlledAutocompleteFacility
                  name='activeFacility'
                  control={control}
                  motorCarrierId={motorCarrierId}
                  AutocompleteFacilityProps={{
                    label: 'Active Facility',
                    required: true,
                    placeholder: 'Select Facility',
                    inputValidationMessage: errors?.activeFacility?.message,
                    inputError: !!errors?.activeFacility?.message,
                  }}
                />
              </Box>
            </div>
          )}
          {showEmptyTrailerArrival && (
            <Box marginBottom={2} display='flex' justifyContent='row'>
              <Box marginRight={2} flex='1'>
                <ControlledDatePicker
                  name='emptyTrailerArrivalDate'
                  control={control}
                  DatePickerProps={{
                    required: true,
                    label: 'Empty Trailer Arrival',
                    placeholder: 'mm/dd/yyyy',
                    validationMessage: errors?.emptyTrailerArrivalDate?.message,
                    error: !!errors?.emptyTrailerArrivalDate?.message,
                  }}
                />
              </Box>
              <Box className={classes.timeInput} flex='1'>
                <ControlledTimeInput
                  control={control}
                  name='emptyTrailerArrivalTime'
                  TimeInputProps={{
                    label: 'Time',
                    required: true,
                    invalid: Boolean(errors.emptyTrailerArrivalTime?.message),
                    validationMessage: errors?.emptyTrailerArrivalTime?.message,
                    timeFormat: TIME_INPUT_FORMAT.MILITARY,
                    placeholder: '--:--',
                  }}
                />
              </Box>
            </Box>
          )}
          {showEmptyTrailerReady && (
            <Box marginBottom={2} display='flex' justifyContent='row'>
              <Box marginRight={2} flex='1'>
                <ControlledDatePicker
                  name='emptyTrailerReadyDate'
                  control={control}
                  DatePickerProps={{
                    required: true,
                    label: 'Empty Trailer Ready',
                    placeholder: 'mm/dd/yyyy',
                    validationMessage: errors?.emptyTrailerReadyDate?.message,
                    error: !!errors?.emptyTrailerReadyDate?.message,
                  }}
                />
              </Box>
              <Box className={classes.timeInput} flex='1'>
                <ControlledTimeInput
                  control={control}
                  name='emptyTrailerReadyTime'
                  TimeInputProps={{
                    label: 'Time',
                    required: true,
                    invalid: Boolean(errors.emptyTrailerReadyTime?.message),
                    validationMessage: errors?.emptyTrailerReadyTime?.message,
                    timeFormat: TIME_INPUT_FORMAT.MILITARY,
                    placeholder: '--:--',
                  }}
                />
              </Box>
            </Box>
          )}
          {showLoadNumber && (
            <Box marginBottom={2}>
              <ControlledAutocompleteLoad
                disabled={false}
                name='assignedLoad'
                control={control}
                AutocompleteLoadProps={{
                  label: 'Load Number',
                  required: true,
                  placeholder: 'Select Load Number',
                  disabled: false,
                  inputValidationMessage: errors?.assignedLoad?.message,
                  inputError: !!errors?.assignedLoad?.message,
                  pickupFacilityId: showActiveFacility ? selectedFacility?.id : undefined,
                  filters: {
                    status: [
                      LOAD_STATE['Ready for Dispatch'],
                      LOAD_STATE.Dispatched,
                      LOAD_STATE['At Pickup'],
                      LOAD_STATE.Loading,
                      LOAD_STATE['In Transit'],
                      LOAD_STATE['In Yard'],
                      LOAD_STATE['At Dropoff'],
                      LOAD_STATE.Unloading,
                      LOAD_STATE.TONU,
                      LOAD_STATE.Claim,
                    ],
                    onlyNoTrailer: true,
                  },
                }}
              />
            </Box>
          )}
          <Box marginBottom={2}>
            <ControlledTextInput
              control={control}
              name='notes'
              TextInputProps={{
                label: 'Location Notes (shared with Transfix)',
                placeholder: 'Helpful notes about your trailer location',
                validationMessage: errors?.notes?.message,
                error: !!errors?.notes?.message,
              }}
            />
          </Box>
          <Box className={classes.submitButton}>
            <Button color='primary' size='large' type='submit'>
              Submit
            </Button>
          </Box>
        </Box>
      </form>
    </Card>
  )
}

export default TrailerAssignmentCard
